import { EyeIcon, PencilAltIcon, XIcon } from "@heroicons/react/outline";
import { CheckCircleIcon } from "@heroicons/react/outline";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import ReactTable from "components/lib/Shared/ReactTable";
import useFullscreenToggle from "hooks/useFullscreenToggle";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PdfGenerator from "./pdfGenerator";
import { getIpAddress } from "utils/ipAddress";
import SentIcon from "components/lib/Shared/Icons/SentIcon";
import ViewedIcon from "components/lib/Shared/Icons/ViewedIcon";
import SignedIcon from "components/lib/Shared/Icons/SignedIcon";
import { getHsEventsFromEvelopes, getSigners } from "utils/auditTrial";
import StatusIcon from "../Icons/StatusIcon";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import IpAddressIcon from "../Icons/IpAddressIcon";
import { Constants } from "config/constants";

/**
 * AuditTrailTemplate component displays the audit trail of the e-signature process.
 * It shows the details of the envelopes, signers, and their events.
 * It also provides a PDF preview of the audit trail.
 * @param {*} param0 - The props object
 * @returns
 */
const AuditTrailTemplate = ({ envelopes }) => {
  // --- Redux State ---
  const { company } = useSelector((state) => state.companySlice);
  const currentUser = useSelector((state) => state.userSlice);
  const companyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );
  const { dashboardRoles } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const { organization, dashboardPermissions = [] } = useSelector(
    (state) => state.organizationsSlice
  );

  // --- Local State ---
  const [showIframe, setShowIframe] = useState(false);
  const [pdfIsGenerated, setPdfIsGenerated] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);
  const [status, setStatus] = useState("Complete");
  const [signers, setSigners] = useState([]);

  // --- Derived/Helper Variables ---
  const hsEvents = getHsEventsFromEvelopes(envelopes);
  const applicant = company.createdBy;
  const companyId = company._id;
  const { isFullscreen, ToggleFullscreenButton } = useFullscreenToggle();
  const dateCreated = new Date().toLocaleString("en-GB");
  const envelopeOriginator = `${currentUser?.names?.firstName || ""} ${
    currentUser?.names?.lastName || ""
  }`;
  const signatures = signers.length;
  let envelopeId = "";
  let directorsEnvelopeId = "";
  let shareholdersEnvelopeId = "";

  // --- Permissions ---
  const { DASHBOARD_PERMISSIONS } = Roles_Permissions_Constants;
  const isVORPLUS = dashboardPermissions.some(
    (permission) =>
      permission.name === DASHBOARD_PERMISSIONS.SHOW_REQUEST_BUTTON
  );
  const isVOR = dashboardRoles?.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.VOR
  );

  // --- Envelope IDs and Table Data ---
  const leftColumnValues = [
    { desc: "Company ID", value: companyId },
    { desc: "Envelope Originator", value: envelopeOriginator },
  ];
  if (envelopes.length < 2) {
    envelopeId = envelopes[0]._id;
    leftColumnValues.unshift({ desc: "Envelope ID", value: envelopeId });
  } else {
    directorsEnvelopeId = envelopes[0]._id;
    shareholdersEnvelopeId = envelopes[1]._id;
    leftColumnValues.unshift(
      { desc: "Directors Envelope ID", value: directorsEnvelopeId },
      { desc: "Shareholders Envelope ID", value: shareholdersEnvelopeId }
    );
  }
  const rightColumnValues = [
    { desc: "Date Format", value: "dd/mm/yyyy" },
    { desc: "Date Created", value: dateCreated },
    { desc: "IP Address", value: ipAddress },
    { desc: "Signatures", value: signatures },
  ];

  // --- Utility Functions ---
  const maskEmailAddress = (emailAddress) => {
    if (!emailAddress) return "";
    const firstChar = emailAddress.charAt(0);
    const first10Letters = emailAddress.slice(1, 13);
    const masked = first10Letters.replace(/./g, "*");
    return firstChar + masked + emailAddress.slice(13);
  };

  const updateStatusCode = (status) => {
    if (!status) return "";
    let firstWord = status.split("_")[0];
    return firstWord.charAt(0).toUpperCase() + firstWord.slice(1);
  };

  const generateTableDataForSigner = (signer) => {
    const signatureRequestSent = hsEvents.find(
      (callbackData) =>
        callbackData.event.event_type === "signature_request_sent"
    );
    const sentTime = signatureRequestSent?.event.event_time;
    const signerEvents = [
      `Sent: ${
        sentTime ? new Date(sentTime * 1000).toLocaleString("en-GB") : "N/A"
      }`,
    ];
    if (signer?.last_viewed_at) {
      signerEvents.push(
        `Viewed: ${new Date(signer.last_viewed_at * 1000).toLocaleString(
          "en-GB"
        )}`
      );
    }
    if (signer?.signed_at) {
      signerEvents.push(
        `Signed: ${new Date(signer.signed_at * 1000).toLocaleString("en-GB")}`,
        `IP Address: ${signer.ipAddress}`
      );
    }
    return {
      userDetails: `${signer.signer_name}\n${signer?.designation || ""}`,
      email:
        isVOR ||
        isVORPLUS ||
        [
          Constants.ROLES.SUPER_ADMIN,
          Constants.ROLES.ORG_ADMIN,
          Constants.ROLES.SYSTEM_ADMIN,
        ].includes(currentUser?.role)
          ? signer.signer_email_address
          : maskEmailAddress(signer.signer_email_address),
      events: signerEvents.map((event) => event + "\n").toString(),
      status: updateStatusCode(signer?.status_code),
    };
  };

  // --- PDF Data ---
  const pdfHeaderData = {
    companyId,
    envelopeId,
    dateCreated,
    ipAddress,
    envelopeOriginator,
    signatures,
    status,
    directorsEnvelopeId,
    shareholdersEnvelopeId,
    dateFormat: "dd/mm/yyyy",
  };
  const pdfTableData = signers.map((signer) =>
    generateTableDataForSigner(signer)
  );

  // --- PDF Generation ---
  const generatePdf = () => {
    PdfGenerator({ pdfHeaderData, pdfTableData });
  };

  // --- Iframe Toggle ---
  const handleIframeToggle = () => {
    if (!showIframe && !pdfIsGenerated) {
      generatePdf();
      setPdfIsGenerated(true);
    }
    setShowIframe((prev) => !prev);
  };

  // --- Effects ---
  useEffect(() => {
    (async () => {
      setIpAddress(await getIpAddress());
    })();
  }, []);

  useEffect(() => {
    (async () => {
      let fetchedSigners = await getSigners(hsEvents);
      if (
        fetchedSigners.some(
          (signer) => signer.status_code === "awaiting_signature"
        )
      ) {
        setStatus("Ongoing");
      }
      fetchedSigners = fetchedSigners.map((signer) => {
        if (signer.signer_name.split("(isCompany")[1]) {
          signer = {
            ...signer,
            signer_name: signer.signer_name.split("(isCompany")[0],
          };
        }
        return signer;
      });
      fetchedSigners = fetchedSigners.map((signer) => {
        if (applicant.names?.firstName === signer.signer_name.split(" ")[0]) {
          signer = { ...signer, designation: "Applicant" };
        }
        companyOfficials.forEach((official) => {
          if (
            official?.names?.firstName === signer.signer_name.split(" ")[0] ||
            official?.names?.companyName === signer.signer_name
          ) {
            signer = { ...signer, designation: official.designation };
          }
        });
        return signer;
      });
      setSigners(fetchedSigners);
    })();
  }, []);

  // --- Table Columns ---
  const columns = [
    {
      Header: "Signer Details",
      accessor: "signerDetails",
      Cell: ({ row: { original } }) => (
        <div className="flex items-center space-x-1 w-52">
          <div className="flex-shrink-0">
            {original.signer_name}
            <p>
              <span className="items-center gap-x-1.5 text-xs font-medium w-fit bg-slate-200 rounded px-3 py-1 tracking-wide">
                {original?.designation}
              </span>
            </p>
          </div>
        </div>
      ),
    },
    {
      Header: "Email",
      accessor: "email",
      Cell: ({ row: { original } }) => (
        <div className="flex items-center space-x-1 w-52">
          <div className="flex-shrink-0">
            <p>{original.signer_email_address}</p>
          </div>
        </div>
      ),
    },
    {
      Header: "Events",
      accessor: "events",
      Cell: ({ row: { original } }) => {
        const signatureRequestSent = hsEvents.find(
          (callbackData) =>
            callbackData.event.event_type === "signature_request_sent"
        );
        const sentTime = signatureRequestSent?.event.event_time;
        let originalEvents = [
          {
            title: "Sent",
            icon: <SentIcon />,
            value: sentTime
              ? new Date(sentTime * 1000).toLocaleString("en-GB")
              : "N/A",
          },
        ];
        if (original?.last_viewed_at) {
          originalEvents.push({
            title: "Viewed",
            icon: <ViewedIcon />,
            value: new Date(original.last_viewed_at * 1000).toLocaleString(
              "en-GB"
            ),
          });
        }
        if (original?.signed_at) {
          originalEvents.push({
            title: "Signed",
            icon: <SignedIcon />,
            value: new Date(original.signed_at * 1000).toLocaleString("en-GB"),
          });
        }
        if (original?.ipAddress) {
          originalEvents.unshift({
            title: "Ip Address",
            icon: <IpAddressIcon />,
            value: original.ipAddress,
          });
        }
        return (
          <div className="flex items-center space-x-1 w-52">
            <div className="flex-shrink-0">
              {originalEvents.map((event, idx) => (
                <p className="flex justify-start gap-3 my-2" key={idx}>
                  <span className="flex justify-start gap-2">
                    <span>{event.icon}</span>
                    {event.title}
                  </span>
                  <span>{event.value}</span>
                </p>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row: { original } }) => (
        <div className="w-fit bg-slate-200 rounded px-3">
          <div>
            <p>{updateStatusCode(original.status_code)}</p>
          </div>
        </div>
      ),
    },
  ];

  if (!hsEvents) {
    return (
      <div>
        <h1 className="text-xl text-center font-bold rounded bg-white">
          Audit Trail for this company is not available
        </h1>
      </div>
    );
  }

  return (
    <div className="bg-white ">
      <div
        className={
          isFullscreen
            ? "fixed inset-0 bg-white px-4 top-[7.5rem] z-50"
            : "w-full"
        }
      >
        <ToggleFullscreenButton />
        <div className="text-end p-3 ">
          <button onClick={handleIframeToggle}>
            {showIframe ? (
              <span>
                <XIcon className="inline w-5 h-5 mr-0" /> Close
              </span>
            ) : (
              <span>
                <EyeIcon className="inline w-6 h-6 mr-1" /> Preview PDF
              </span>
            )}
          </button>
        </div>
        {showIframe ? (
          <iframe
            name="theFrame"
            style={{ width: "100%", height: "800px" }}
          ></iframe>
        ) : (
          <iframe name="theFrame" style={{ display: "none" }}></iframe>
        )}
        <div className="px-12 my-5">
          <h1 className="text-md text-end my-3">
            <span className="flex flex-col gap-2">
              <span className="font-semibold">CERTIFICATE OF COMPLETION</span>
              <span className="bg-slate-200 text-sm  px-3 rounded w-fit self-end">
                {status}
              </span>
            </span>
          </h1>
          <div className="w-fit my-3 flex text-sm justify-start bg-slate-200  gap-12 mt-14  p-4 px-6 rounded">
            <div>
              {leftColumnValues.map((colm) => (
                <p className="flex flex-col pb-1" key={colm.desc}>
                  <span className="font-semibold">{colm.desc} </span>
                  <span>{colm.value}</span>
                </p>
              ))}
            </div>
            <div>
              {rightColumnValues.map((colm) => (
                <p className="flex flex-col pb-1" key={colm.desc}>
                  <span className="font-semibold">{colm.desc} </span>
                  <span>{colm.value}</span>
                </p>
              ))}
            </div>
          </div>
        </div>
        <hr className="my-6" />
        <div className="px-12">
          <ReactTable
            columns={columns}
            data={signers}
            dataRowCellStyle="whitespace-normal px-4 py-4"
          />
        </div>
      </div>
    </div>
  );
};

export default AuditTrailTemplate;
